/**
 * The file executes on server and client both,
 * hence isServer() check should be done for client only functionalities
 * or they should be called from useEffect.
 */

import { LinkHTMLAttributes, useEffect, useState } from 'react';

import { type IMutableContext } from '@unleash/nextjs';
import type { AppContext, AppProps } from 'next/app';
import App from 'next/app';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';

import Footer from 'src/components/footer/Footer';
import { ToastMessageGcpErrorWrapper } from 'src/components/toastMessage/ToastMessageGcpErrorWrapper';
import { launchApiHelper } from 'src/services/apiRequests/launchApiHelper';
import { useAppStore } from 'src/hooks/useAppStore';
import { AppStoreProvider } from 'src/providers/appStoreProvider';
import { fetchTelcoUserToken } from 'src/services/apiRequests/Login/telco-user-login/ShortToLongTokenService';
import {
  getActiveSubscription,
  getDisplayPlansMetaData
} from 'src/services/apiRequests/SubscriptionService';
import { fetchFeatureFlags } from 'src/services/remoteconfig';
import { UserTokenType } from 'src/services/responseTypes/fetchTelcoUserTokenResponse';
import { SingleLaunchResponse } from 'src/services/responseTypes/singleLaunchResponse';
import { getDefaultServerSideState } from 'src/store/CreateStore';
import { AppActions, AppState } from 'src/store/interface';
import { mixpanelObject } from 'src/utils/analytics';
import {
  initialDataPropertySetter,
  initializeMixpanelSessionID
} from 'src/utils/analytics/mixpanel/mixpanelHelper';
import { logout } from 'src/utils/auth-utils/LogoutHandler';
import {
  getUserTokenFromLS,
  setRefreshTokenInLS,
  setUserTokenExpiryInLS,
  setUserTokenInLS
} from 'src/utils/auth-utils/TokenHandler';
import {
  getUserTypeFromLS,
  setUserTypeInLS
} from 'src/utils/auth-utils/UserTypeHandler';
import { checkIfRequestedByBot } from 'src/utils/bot-utils/CheckIfRequestedByBot';
import { getBrowserType } from 'src/utils/browser-type';
import { getDeviceOS } from 'src/utils/CheckDeviceType';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  DEVICE_TYPES,
  GUEST_TOKEN,
  LOGIN_STATUS,
  MEMBERSHIP_TYPES
} from 'src/utils/constants/AppConstants';
import {
  HTTP_HEADER_KEYS,
  HTTP_URL_PARAMS
} from 'src/utils/constants/HttpMethodsConstants';
import { NavMenuLinks } from 'src/utils/constants/RouterConstants';
import {
  CountryCode,
  DisplayLanguage,
  LAUNCH_ERROR,
  ROUTES,
  UserTypes
} from 'src/utils/constants/stringConstants';
import {
  APP_CONFIG,
  EPISODE_TRANSLATION,
  FeatureFlagConstants,
  INITIAL_LAUNCH,
  REMOTE_CONFIG,
  USER_LOCATION_DATA
} from 'src/utils/constants/utilConstants';
import { getDeviceWidth } from 'src/utils/getDeviceWidth';
import { checkAvifCompatible } from 'src/utils/ImageHelper/checkAvifCompatibility';
import {
  ONE_TRUST_COOKIE_CONTRIES,
  oneTrustInitialization,
  oneTrustLoad,
  oneTrustScript
} from 'src/utils/oneTrustHelper';
import { isFeatureEnabled } from 'src/utils/RemoteConfig';
import { urlDisplayLanguageHandler } from 'src/utils/urlDisplayLanguageHandler';
import { isValidCountry } from 'src/utils/user-location-utils/CountryValidation';

import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import Fallback from '../components/Fallback/Fallback';
import Header from '../components/header/Header';
import CommonSEOMeta from '../components/pageMeta/CommonSEOMeta/CommonSEOMeta';
import { initialLoadIcons } from '../fonts/customFontsConfig';
import { useConstructor } from '../hooks/useConstructor';
import { setGuestUserSettings } from '../services/apiRequests/LandingPage/guestUserFlow';
import { getRegisteredUserSettings } from '../services/apiRequests/LandingPage/registeredUserFlow';
import {
  getTranslations,
  isTranslationDifferent
} from '../services/apiRequests/TranslationsService';
import {
  checkAVIFSuport,
  checkWebpSupport,
  fetchLaunchAPIData,
  getTrueDeviceType,
  getUserLocation
} from '../services/appLaunch';
import SimpleStore from '../store/SimpleStore';
import { globalStyles, MainWrapper } from '../styles/styles';
import { timeEvent } from '../utils/analytics/mixpanel/mixpanelEventsHelper';
import {
  deleteLocalStorage,
  getLocalStorage,
  setCookie,
  setLocalStorage,
  setSessionStorage,
  storeMixPanelMetricsInSS
} from '../utils/browserStorageHandler';
import { isConsumptionPageCheck } from '../utils/checkConsumptionPage';
import {
  isServer,
  isWebView,
  setLandingPage,
  storeUtmData
} from '../utils/common';
import {
  CONTENT_LANGUAGES_COOKIE,
  COUNTRY,
  DISPLAY_LANGUAGE_COOKIE,
  DROPDOWN_COUNTRY,
  USER_TYPE_COOKIE,
  USER_TYPE_LAUNCH_API,
  XACCESS_AUTHORIZATION_COOKIE
} from '../utils/constants/cookieConstants';
import { Fallback_Type_Constants } from '../utils/constants/FallbackTypeConstants';
import { LAUNCH_USER_TYPES } from '../utils/constants/LaunchApiConstants';
import {
  IS_ACTIVE_SUBSCRIPTION_FOUND,
  OPEN_PURCHASE_POPUP,
  PREVIOUS_CONSUMPTION_PAGE_LANGUAGES,
  RSJ_SUBSCRIPTION_PAYMENT_NOT_LINKED,
  SETTINGS,
  SUBSCRIPTION_PAYMENT_NOT_LINKED,
  USER_ID,
  USER_TOKEN,
  UTM_SOURCE
} from '../utils/constants/localStorageConstants';
import {
  getGuestUserPaymentTransactionId,
  getTempUserTransactionId,
  isGuestUserPaymentSuccess
} from '../utils/ConsumptionHelper/BuyPlanHelper';
import { getCookieDataOnServerSide } from '../utils/cookieHelper';
import { globalRedirectionHandler } from '../utils/globalRedirectionHelper';
import { checkWebPCompatible } from '../utils/ImageHelper/checkWebPCompatibility';
import { getIntitialContentAndDisplayLanguage } from '../utils/LanguageUtils';
import { MixpanelScriptAdder } from '../utils/scripts/thirdPartyScripts';
import {
  getGuestUserToken,
  getPlatformName,
  isCountryDropdownEnabled,
  updateBotValue
} from '../utils/UtilityMethods';
import { validateInitialDataOnServer } from '../utils/validateInitialData';
import { fetchEntitlementUserType } from 'src/utils/fetchEntitlementUserType';

const GenericPopupWrap = dynamic(
  () => import('../components/genericPopupWrap'),
  {
    loading: () => null,
    ssr: false
  }
);

const ViTvodBlockerPopup = dynamic(
  () => import('../components/viTvodBlockerPopup/ViTvodBlockerPopup'),
  {
    ssr: false
  }
);

const UserOnboard = dynamic(() => import('../components/userOnboard'), {
  ssr: false
});

export interface LaunchApiParams {
  state: string;
  translation: string;
  version: number;
  ccode: string;
  country: string;
  platform_name: string;
}

type TProps = Pick<AppProps, 'Component' | 'pageProps'> & {
  initialServerSideState: AppState & AppActions;
  isInitialDataValid: boolean;
  apiErrorResponse: Error & { errData: { [key: string]: DynamicObject } };
  context: IMutableContext;
};

interface CustomLinkAttributes extends LinkHTMLAttributes<HTMLLinkElement> {
  fetchpriority?: string;
}

const preloadFallbackImageLinkProps: CustomLinkAttributes = {
  rel: 'preload',
  fetchpriority: 'high',
  as: 'image',
  href: '/webapp-assets/images/fallback_portrait.svg',
  type: 'image/webp'
};

function MyApp({
  Component,
  pageProps,
  initialServerSideState,
  isInitialDataValid,
  apiErrorResponse,
  context
}: TProps) {
  const {
    initialLaunchData,
    translations,
    usersLocationData,
    appConfig,
    usersPreferenceData,
    subscriptionData,
    setRemoteConfig,
    isUserLoggedIn,
    setAppConfig,
    setTranslations,
    setUsersPreferenceData,
    setUsersData,
    setIsUserLoggedIn,
    setErrorData,
    setSubscriptionData,
    setUserPackType
  } = useAppStore();
  //initializing store with server state
  useConstructor(() => {
    SimpleStore.set(
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
      initialServerSideState?.initialLaunchData?.platform_token?.token || ''
    );
    SimpleStore.set(
      HTTP_HEADER_KEYS.AUTHORIZATION,
      getLocalStorage(USER_TOKEN) || ''
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.COUNTRY_CODE,
      initialServerSideState?.usersLocationData?.country_code || ''
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
      initialServerSideState?.usersPreferenceData?.display_language ||
        DisplayLanguage.ENGLISH
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.CONTENT_LANGUAGE,
      initialServerSideState?.usersPreferenceData?.content_language ||
        DisplayLanguage.ENGLISH
    );
    SimpleStore.set(
      USER_LOCATION_DATA,
      initialServerSideState?.usersLocationData || {}
    );
    SimpleStore.set(HTTP_HEADER_KEYS.X_Z5_APP_PLATFORM, getPlatformName(true));
    SimpleStore.set(
      HTTP_HEADER_KEYS.X_Z5_APP_VERSION,
      process.env.APP_VERSION || 'NA'
    );
    SimpleStore.set(APP_CONFIG, initialServerSideState?.appConfig || {});
  });

  // let sss;
  // const sss1 = hook.subscribe((state) => {
  //   sss = state;
  //   console.log('subscribe', state);
  // });

  // console.log('sss', sss);

  const oneTrustActiveGroupsLength =
    typeof window !== 'undefined' && window?.OnetrustActiveGroups?.length;

  const router = useRouter();
  const { query } = router;

  const countryCode =
    usersLocationData?.country_code ??
    SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE) ??
    CountryCode.IN;
  const displayLanguage =
    usersPreferenceData?.display_language ?? DisplayLanguage.ENGLISH;

  useEffect(() => {
    if (query?.cjevent) {
      setCookie('cje', `${query?.cjevent}`, 395);
    }
  }, [query]);

  useEffect(() => {
    oneTrustLoad(router.asPath, countryCode);
  }, [oneTrustActiveGroupsLength]);

  const fetchUserAuthToken = async (tag: string) => {
    let token: UserTokenType = {
      user: { token: '', expires_in: 0, token_type: '', refresh_token: '' }
    };
    try {
      token = await fetchTelcoUserToken(tag);
      //TODO: Error handling as it can respond with 401 when tag is expired.
      if (token?.user?.token) {
        setUserTokenInLS(token.user.token);
        setUserTokenExpiryInLS(token.user.expires_in);
        setRefreshTokenInLS(token.user.refresh_token);
        setIsUserLoggedIn(true);
      }
    } catch (error) {
      console.error('Error while fetching telco user token', error);
    }
    return token?.user?.token;
  };

  useEffect(() => {
    /**
     * This is used to open PlexRentNowPopUp when ios if it is a tvod content
     */
    if (
      router.asPath.includes('/movies/details') &&
      router.query?.utm_source === 'ios' &&
      router.query?.utm_medium === 'tvod_purchase' &&
      getLocalStorage(OPEN_PURCHASE_POPUP) !== 'true'
    ) {
      setLocalStorage(OPEN_PURCHASE_POPUP, true);
    }

    // to store the utm_source utm_medium utm_campaign utm_content utm_term and used in mixpanel
    storeUtmData(router);
  }, []);

  useEffect(() => {
    /**
     * This block of code is required for handling TVOD flow of IOS. This code is similar to B2B handling
     * with only difference of utm_medium. It should only execute if URL has tag and utm medium is tvod_purchase as sent by IOS
     *
     * Other than that, for normal consumption urls and for B2B urls, this should not execute.
     *
     * IOS forms different URLs based on usertype. If user_type is free, they send guest_token instead of tag in URL, otherwise we get tag.
     */
    //TODO: revisit to improve code quality. As of now, duplicate of B2B handling.
    const { tag, utm_medium, user_type, utm_source, guest_token } =
      router.query;
    if ((tag || guest_token) && utm_medium === 'tvod_purchase') {
      if (getUserTokenFromLS()) {
        logout();
      }
      (async () => {
        await getGuestUserToken();
        await fetchUserAuthToken(tag as string);
        setAppConfig({ userType: user_type as string });
        setUserTypeInLS(user_type as string);
        setSessionStorage(UTM_SOURCE, utm_source);
      })();
      const shortPathnameToShow = router.asPath.split('?')[0];
      router?.replace(router.asPath, shortPathnameToShow, { shallow: true });
    }
  }, [router.asPath]);

  const locale = router.locale ?? 'en';

  const setTransaltionsAndSettings = async () => {
    const token = getLocalStorage(USER_TOKEN);
    let currentDisplayLanguage = locale;
    if (token) {
      const subscriptionDataResponse = await getActiveSubscription(countryCode);
      if (subscriptionDataResponse) {
        setSubscriptionData({
          ...subscriptionData,
          ...subscriptionDataResponse
        });
      }
      //TODO: usertype should be based on current active subscription.
      const maxPricePack = subscriptionDataResponse?.maxPricePack;
      let hasActiveSubscription = false;
      if (
        subscriptionDataResponse &&
        subscriptionDataResponse.currentActiveSubscriptions &&
        subscriptionDataResponse.currentActiveSubscriptions.length > 0
      ) {
        hasActiveSubscription = true;
      }
      setUserTypeInLS(
        hasActiveSubscription ? UserTypes.PREMIUM : UserTypes.REGISTER
      );
      setCookie(
        USER_TYPE_LAUNCH_API,
        maxPricePack
          ? LAUNCH_USER_TYPES.SUBSCRIBED
          : LAUNCH_USER_TYPES.REGISTERED
      );
      if (maxPricePack) {
        const packType = fetchEntitlementUserType(maxPricePack);
        if (packType) {
          setUserPackType(packType);
        }
      }

      const userInitialData = usersPreferenceData;
      // usersPreferenceData ||
      // (await getInitialUserData(setUsersPreferenceData));

      setIsUserLoggedIn(true);
      const settings = await getRegisteredUserSettings(locale, userInitialData);
      if (settings === 401) {
        setIsUserLoggedIn(false);
        setUserTypeInLS(UserTypes.GUEST);
        setGuestUserSettings(locale, initialLaunchData!);
        setTranslations(translations);
      } else {
        setUsersPreferenceData({ ...settings });
        const displayLanguage = settings?.display_language;
        if (displayLanguage && locale !== displayLanguage) {
          //updating translation based user's language preference.
          currentDisplayLanguage = displayLanguage;
        }
        if (settings?.display_language) {
          SimpleStore.set(
            HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
            settings.display_language
          );
        }

        if (settings?.content_language) {
          SimpleStore.set(
            HTTP_URL_PARAMS.CONTENT_LANGUAGE,
            settings.content_language
          );
        }
      }
    } else {
      setIsUserLoggedIn(false);
      setUserTypeInLS(UserTypes.GUEST);
      setGuestUserSettings(locale, initialLaunchData!);
      const settingsJson = getLocalStorage(SETTINGS);
      let settings;
      if (settingsJson) {
        try {
          settings = JSON.parse(settingsJson);
        } catch (error) {
          console.error('Error parsing settings JSON:', error);
        }

        if (appConfig?.isRequestedByBot) {
          if (settings) {
            settings.content_language = 'hi,en,mr,te,kn,ta,ml,bn,gu,pa,hr,or';
          }
        }
        SimpleStore.set(
          HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
          settings.display_language
        );
        SimpleStore.set(
          HTTP_URL_PARAMS.CONTENT_LANGUAGE,
          settings.content_language
        );
        setUsersPreferenceData({ ...(settings || {}) });
      }
    }

    const s3Translations = await getTranslations(
      currentDisplayLanguage as string
    );
    if (
      currentDisplayLanguage !== locale ||
      isTranslationDifferent(s3Translations, translations)
    ) {
      setTranslations(s3Translations);
    }
  };

  const deviceOrientationChangeHandler = () => {
    //TODO: Not supported on safari ios need to find alternate
    const orientation = window?.screen?.orientation;
    if (orientation) {
      orientation.addEventListener('change', (e) => {
        setAppConfig({ deviceWidth: getDeviceWidth() });
      });
    }
  };

  //Along with other places, it is also required in B2B. Ideally, to be part of login

  useEffect(() => {
    checkWebPCompatible(appConfig?.isWebPCompatible || false);
    checkAvifCompatible(appConfig?.isAvifCompatible || false);
    updateBotValue(appConfig?.isRequestedByBot || false);
    setTransaltionsAndSettings();
    deviceOrientationChangeHandler();
    if (window) {
      window.storeMixPanelMetricsInSS = storeMixPanelMetricsInSS;
    }
    MixpanelScriptAdder(countryCode);
    initializeMixpanelSessionID(mixpanelObject);
    initialDataPropertySetter(mixpanelObject);
    timeEvent();
    setLandingPage(usersLocationData?.country?.toUpperCase() || 'N/A');
    setErrorData(apiErrorResponse?.errData);
    oneTrustInitialization();
  }, []);

  useEffect(() => {
    //Update store config with actual device width.
    setAppConfig({ deviceWidth: getDeviceWidth() as number });

    // plex landing is not showing in Germany
    if (
      initialLaunchData?.country_config?.code === CountryCode.DE &&
      router.asPath === NavMenuLinks.PLEX
    ) {
      window.location.assign('/');
    }
  }, []);

  useEffect(() => {
    /**
     * Clear previous consumption page language from session storage
     * This will clear language change popup on page reload
     */
    setSessionStorage(PREVIOUS_CONSUMPTION_PAGE_LANGUAGES, '');
  }, []);
  // TODO: check if we can do that on the bases of requestByPatner flag of store.
  const shouldShowHeaderAndFooter = () => {
    if (
      router.asPath.includes('/b2b-consumption/') ||
      router.asPath.includes('/know-your-team') ||
      router.asPath.includes('/b2b-live-tv/') ||
      router.asPath.includes('contactus') ||
      isWebView()
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    const userType = getUserTypeFromLS();
    //If context for kraken is updated, we need to create the context and call the function again.
    fetchFeatureFlags({
      /** updated context */
      ...context,
      userId:
        userType === UserTypes.GUEST
          ? getLocalStorage(GUEST_TOKEN)
          : getLocalStorage(USER_ID),
      properties: {
        loginStatus:
          userType === UserTypes.GUEST
            ? LOGIN_STATUS.GUEST
            : LOGIN_STATUS.LOGGEDIN,
        membershipType:
          userType === UserTypes.PREMIUM
            ? MEMBERSHIP_TYPES.SVOD
            : MEMBERSHIP_TYPES.AVOD,
        platform: getDeviceOS(),
        appVersion: process.env.APP_VERSION || '4.1.3',
        browser: getBrowserType(),
        ...context.properties
      }
    })
      .then((toggles) => {
        /** update store */
        setRemoteConfig(toggles);
      })
      .catch((error) => {
        console.log('Error while fetching remote config', error);
      });
  }, []);

  const [userLoginFlag, setUserLoginFlag] = useState(false);
  /**
   * For RSJ flow, Checking payment success, order id then setting value for payment linking pending flag to true
   */
  useEffect(() => {
    if (isGuestUserPaymentSuccess() && getGuestUserPaymentTransactionId()) {
      /** RSJ subscription journey, For the first time we will show payment
       * successfull with user to Link phone number, next time onwards will
       * inform user to Link phone number.
       */
      if (getLocalStorage(RSJ_SUBSCRIPTION_PAYMENT_NOT_LINKED)) {
        setLocalStorage(SUBSCRIPTION_PAYMENT_NOT_LINKED, true);
      }
      setLocalStorage(RSJ_SUBSCRIPTION_PAYMENT_NOT_LINKED, true);
      // payment not linked popup will dominate over active subscription found popup
      deleteLocalStorage(IS_ACTIVE_SUBSCRIPTION_FOUND);
    }

    const isConsumptionPaymentSuccess =
      isGuestUserPaymentSuccess() || getTempUserTransactionId();

    if (
      (router.pathname === '/' || isConsumptionPageCheck(router.asPath)) &&
      !userLoginFlag &&
      isConsumptionPaymentSuccess &&
      !isUserLoggedIn
    ) {
      setUserLoginFlag(true);
    }
  }, []);

  const commonSEOProps = {
    displayLanguage,
    pathName: router.asPath,
    countryCode,
    isBotRequest: appConfig.isRequestedByBot || false,
    appHostname: SimpleStore.get(APP_CONFIG)?.appHostname
  };
  const remoteConfig = SimpleStore.get(REMOTE_CONFIG);

  const isNewSubscription =
    isFeatureEnabled(
      remoteConfig,
      FeatureFlagConstants.FEATURE_NEW_SUBSCRIPTION_UI
    ) && router.asPath.includes('/myaccount/subscription');

  return (
    <>
      <Head>
        <meta name="theme-color" content="#1d001e" />
        <meta
          name="viewport"
          content="width=device-width,initial-scale=1,user-scalable=no,shrink-to-fit=no"
        />
        <meta
          name="facebook-domain-verification"
          content="ysxdlsijfp0rxdl1nrdg2gp2b1kqmu"
        />
        {appConfig.appHostname &&
          !(
            appConfig.appHostname === 'zee5.com' ||
            appConfig.appHostname === 'www.zee5.com'
          ) && <meta name="robots" content="noindex, nofollow" />}
        {ONE_TRUST_COOKIE_CONTRIES.includes(countryCode) && oneTrustScript()}
        {isConsumptionPageCheck(router.asPath) && (
          // Pre load fallback thumbnail for consumption pages
          <link {...preloadFallbackImageLinkProps} />
        )}
      </Head>

      {globalStyles}

      {!isInitialDataValid && isServer() ? (
        // Render fallback component if initial data is not valid
        <Fallback type={Fallback_Type_Constants.SOMETHING_WENT_WRONG} />
      ) : (
        <>
          <AppStoreProvider value={initialServerSideState}>
            {shouldShowHeaderAndFooter() && <Header />}
            <CommonSEOMeta {...commonSEOProps} />
            <ErrorBoundary
              isHeaderPresent={
                shouldShowHeaderAndFooter() && !isNewSubscription
              }>
              <MainWrapper className={`${initialLoadIcons.variable}`}>
                <Component {...pageProps} />
                {/* TODO: Rename GenericPopupWrap to parental pip pop-up */}
                <GenericPopupWrap />
                {appConfig.isViUser && <ViTvodBlockerPopup />}
                {userLoginFlag && <UserOnboard />}
                {<ToastMessageGcpErrorWrapper />}
              </MainWrapper>
            </ErrorBoundary>
            {shouldShowHeaderAndFooter() && <Footer />}
          </AppStoreProvider>
        </>
      )}
    </>
  );
}

export default MyApp;
// to do need expertise to solve this complexity
// eslint-disable-next-line cyclomatic-complexity/zee-codeBlockComplexity
MyApp.getInitialProps = async (appContext: AppContext) => {
  const pageProps = await App.getInitialProps(appContext);
  const appHostname = appContext.ctx.req?.headers.host;
  const url = appContext.ctx.req?.url || '';

  const pathsToExclude =
    /^(\/_next\/|\/webapp-assets\/|\/service-worker-webapp-|\/workbox-|\/webapp-translations\/)/;

  //To prevent multiple APi calls on dev for static assets
  if (url && pathsToExclude.test(url)) {
    return {};
  }
  const requestHeaders = appContext.ctx.req;
  const cookieData = requestHeaders?.headers.cookie as string;
  const userType = getCookieDataOnServerSide(cookieData, USER_TYPE_COOKIE);
  const countryCookie = getCookieDataOnServerSide(cookieData, COUNTRY);
  const router = appContext.router;
  const locale = appContext.router.locale || 'en';
  const userAgent = requestHeaders?.headers['user-agent'] as string;
  const isRequestedByBot =
    requestHeaders?.headers.requested_by_searchbot === 'true' ||
    checkIfRequestedByBot(userAgent);

  const isWebPCompatible = checkWebpSupport(requestHeaders);
  const isAvifCompatible = checkAVIFSuport(requestHeaders);

  /***   Following headers give us request ip  ***\
         * In case where cloudflare can't be disabled  *
         * true-client-ip may not give correct ip or   *
         * might be disabled for such scenarios we can *
        \* use cf-connecting-ip from req headers.     **/
  const ip =
    requestHeaders?.headers['true-client-ip'] ||
    (requestHeaders?.headers['x-real-ip'] as string)?.split(',')[0] ||
    requestHeaders?.headers['x-forwarded-for'] ||
    requestHeaders?.connection.remoteAddress?.substring(7) ||
    requestHeaders?.socket.remoteAddress?.substring(7) ||
    '';

  const locationRes = await getUserLocation(
    ip,
    countryCookie,
    requestHeaders?.headers['x-akamai-edgescape']
  );
  const actualDeviceType = getTrueDeviceType(requestHeaders?.headers);
  let deviceType = actualDeviceType;
  const deviceWidth =
    deviceType === DEVICE_TYPES.MOBILE
      ? 480
      : deviceType === DEVICE_TYPES.DESKTOP
      ? 1200
      : 1024;

  const dropDownCountry = getCookieDataOnServerSide(
    cookieData,
    DROPDOWN_COUNTRY
  );

  if (locationRes) {
    locationRes.country_code =
      isCountryDropdownEnabled(requestHeaders?.headers?.host) && dropDownCountry
        ? dropDownCountry
        : locationRes?.country_code;
  }

  if (
    !countryCookie ||
    !isValidCountry(countryCookie) ||
    countryCookie != locationRes?.country_code
  ) {
    appContext.ctx.res?.setHeader(
      'Set-Cookie',
      `${COUNTRY}=${locationRes?.country_code}; path=/`
    );
  }

  const context = {
    appName: 'webapp',
    environment: 'default',
    properties: {
      country: locationRes.country_code,
      city: locationRes.city,
      state: locationRes.state
    },
    currentTime: new Date().toISOString()
  };
  const toggles = await fetchFeatureFlags(context);
  /**
   * Hardcode the country and state codes for bots based on URL to India or US
   * Due to copyright 0-6-4z5311140 is hardcoded to SG, and other global to US
   */

  if (isRequestedByBot) {
    if (router.asPath.startsWith('/global')) {
      if (router.asPath.includes('0-6-4z5311140')) {
        locationRes.country_code = 'SG';
        locationRes.state_code = 'ENG';
      } else {
        locationRes.country_code = 'US';
        locationRes.state_code = 'ENG';
      }
    } else {
      locationRes.country_code = 'IN';
      locationRes.state_code = 'MH';
    }
    deviceType = DEVICE_TYPES.MOBILE;
  }
  let partnerName;
  if (router.query.tag && router.query.partner) {
    partnerName = router.query.partner;
  }

  const singleLaunchApiParams = {
    stateCode: locationRes?.state_code || '',
    countryCode: locationRes?.country_code || '',
    platformName: getPlatformName(),
    partnerName: partnerName?.toString(),
    userTypeLaunchApi:
      getCookieDataOnServerSide(cookieData, USER_TYPE_LAUNCH_API) ||
      LAUNCH_USER_TYPES.GUEST,
    appContext: appContext
  };

  let singleLaunchResponse: SingleLaunchResponse | undefined, translations;

  try {
    const [singleLaunchResponseData, translationsData] = await Promise.all([
      launchApiHelper(singleLaunchApiParams),
      getTranslations(locale, true)
      // fetchOfferJSONData()
    ]);
    [singleLaunchResponse, translations] = [
      singleLaunchResponseData,
      translationsData
    ];
  } catch (error) {
    console.error('Launch api or transalations error', error);
  }

  if (!singleLaunchResponse?.platform_token?.token) {
    singleLaunchResponse = await launchApiHelper(singleLaunchApiParams);
  }

  const displayLanguageCookie = getCookieDataOnServerSide(
    cookieData,
    DISPLAY_LANGUAGE_COOKIE
  );
  const contentLanguageCookie = getCookieDataOnServerSide(
    cookieData,
    CONTENT_LANGUAGES_COOKIE
  );

  //Global handlings
  if (!pathsToExclude.test(url) && appContext.ctx.res) {
    const redirectionUrl = globalRedirectionHandler(
      router.asPath,
      locationRes.country_code,
      isRequestedByBot
    );
    const redirectionUrlWithDisplayLanguage = urlDisplayLanguageHandler(
      redirectionUrl,
      router.locale as string,
      url,
      displayLanguageCookie
    );
    if (redirectionUrlWithDisplayLanguage !== url) {
      appContext.ctx.res.writable &&
        appContext.ctx.res?.writeHead(301, {
          Location: redirectionUrlWithDisplayLanguage
        });
      appContext.ctx.res?.end();
      return { ...pageProps, toggles, context };
    }
  }
  // sets the XSS protection header
  appContext.ctx.res?.setHeader('X-XSS-Protection', '1; mode=block');
  appContext.ctx.res?.setHeader('device-category', deviceType);

  let plansMetaData = null;
  if (
    singleLaunchResponse?.platform_token?.token &&
    url?.includes(ROUTES.subscription)
  ) {
    try {
      plansMetaData = await getDisplayPlansMetaData();
    } catch (error) {
      console.error('Error while fetching plans data', error);
    }
  }
  if (singleLaunchResponse?.platform_token?.token) {
    appContext.ctx.res?.setHeader(
      'Set-Cookie',
      `${XACCESS_AUTHORIZATION_COOKIE}=${singleLaunchResponse?.platform_token?.token}; path=/`
    );
  }

  const {
    displayLanguage: defaultDisplayLanguage,
    contentLanguage: defaultContentLanguage
  } = getIntitialContentAndDisplayLanguage(singleLaunchResponse);

  const initialState = {
    initialLaunchData: singleLaunchResponse,
    usersPreferenceData: {
      display_language:
        displayLanguageCookie ||
        defaultDisplayLanguage ||
        DisplayLanguage.ENGLISH,
      content_language:
        contentLanguageCookie ||
        defaultContentLanguage ||
        DisplayLanguage.ENGLISH
    },
    translations: translations,
    userWatchlist: {
      episodes: [],
      movies: [],
      videos: [],
      sports: [],
      plex: [],
      isWatchlistDataFetched: false
    },
    usersData: null,
    isUserLoggedIn:
      userType === UserTypes.REGISTER || userType === UserTypes.PREMIUM,
    remoteConfig: toggles,
    plansMetaData,
    usersLocationData: locationRes,
    appConfig: {
      deviceType,
      deviceWidth,
      userType,
      isWebPCompatible,
      isAvifCompatible,
      isRequestedByBot,
      appHostname,
      actualDeviceType
    },
    parentalData: { showParentalPinPageFlag: false },
    popups: {
      showLanguageBanner: false,
      showLanguagePopup: false,
      isViTvod: false,
      isAvodPopupNudgeVisible: false,
      showReferralSubscriptionNudge: false,
      referralRedirectLink: '',
      referralSourceElement: ''
    },
    subscriptionData: {
      currentActiveSubscriptions: [],
      maxPricePack: {}
    }
  };

  const isInitialDataValid = validateInitialDataOnServer(
    singleLaunchResponse as SingleLaunchResponse,
    locationRes,
    translations
  );

  SimpleStore.cache = {};
  let apiErrorResponse = {};
  if (SimpleStore.get(LAUNCH_ERROR)) {
    apiErrorResponse = SimpleStore.get(LAUNCH_ERROR);
  }
  if (isInitialDataValid) {
    SimpleStore.set(
      HTTP_HEADER_KEYS.X_ACCESS_TOKEN,
      singleLaunchResponse?.platform_token?.token || ''
    );
    SimpleStore.set(HTTP_URL_PARAMS.COUNTRY_CODE, locationRes.country_code);
    SimpleStore.set(
      HTTP_URL_PARAMS.DISPLAY_LANGUAGE,
      displayLanguageCookie || defaultDisplayLanguage || DisplayLanguage.ENGLISH
    );
    SimpleStore.set(
      HTTP_URL_PARAMS.CONTENT_LANGUAGE,
      contentLanguageCookie || defaultContentLanguage || DisplayLanguage.ENGLISH
    );
    SimpleStore.set(REMOTE_CONFIG, toggles);
    SimpleStore.set(USER_LOCATION_DATA, locationRes.state_code);
    SimpleStore.set(APP_CONFIG, initialState?.appConfig || {});
    SimpleStore.set(INITIAL_LAUNCH, initialState?.initialLaunchData || {});
    SimpleStore.set(EPISODE_TRANSLATION, translations?.episodes);
  }

  const serverState = { ...getDefaultServerSideState, ...initialState };

  return {
    ...pageProps,
    initialServerSideState: serverState,
    isInitialDataValid,
    apiErrorResponse,
    context
  };
};
